import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Navigation } from '../components/Navigation';
import { motion } from 'framer-motion';
import {
  Code2,
  Shield,
  Zap,
  Activity,
  CheckCircle2,
  ArrowRight,
  Database,
  Lock,
  Scan,
  Brain,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { ROICalculatorSection } from '../components/demos/ROICalculatorSection';

export const EnterpriseApiPage: React.FC = () => {
  const navigate = useNavigate();

  const menuItems = [
    { label: 'Features', href: '/#features' },
    { label: 'About Us', href: '/about' },
    { label: 'Blog', href: '/blog' },
    { label: 'Press', href: '/press' },
    { label: 'Pitch', href: '/pitch' },
    { label: 'Partnerships', href: '/enterprise-api' }
  ];

  const scrollToSection = (sectionId: string) => {
    // Navigate to home page with section hash
    if (window.location.pathname !== '/') {
      navigate(`/#${sectionId}`);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const scrollToTop = () => {
    if (window.location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const curaScanFeatures = [
    {
      icon: <Brain className="w-8 h-8 text-[#003b82]" />,
      title: "AI-Powered Analysis",
      description: "Advanced machine learning algorithms analyze medical imaging with precision, identifying patterns and anomalies that support clinical decision-making."
    },
    {
      icon: <AlertCircle className="w-8 h-8 text-[#003b82]" />,
      title: "Critical Findings Detection",
      description: "Automatically highlights areas requiring immediate attention, ensuring no critical findings are overlooked during initial review."
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-[#003b82]" />,
      title: "Comprehensive Reports",
      description: "Generates detailed summaries with key observations, potential diagnoses, and recommendations for further evaluation."
    },
    {
      icon: <Zap className="w-8 h-8 text-[#003b82]" />,
      title: "Instant Processing",
      description: "Upload and receive preliminary analysis within seconds, accelerating diagnostic workflows without compromising accuracy."
    },
    {
      icon: <Shield className="w-8 h-8 text-[#003b82]" />,
      title: "Clinical Decision Support",
      description: "Works alongside physicians as an intelligent assistant, reducing diagnostic errors and improving patient outcomes."
    },
    {
      icon: <Scan className="w-8 h-8 text-[#003b82]" />,
      title: "Multi-Modality Support",
      description: "Compatible with X-rays, CT scans, MRIs, and other medical imaging formats for comprehensive diagnostic support."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans selection:bg-[#003c83] selection:text-white">
      <Helmet>
        <title>Cura Enterprise – Clinical AI API for Healthcare</title>
        <meta name="description" content="Integrate clinical-grade conversational AI into your platform. Trained on NIH & FDA datasets with HIPAA-compliant infrastructure → Request access" />
        <link rel="canonical" href="https://mycura.org/enterprise-api" />
        <meta property="og:title" content="Cura Enterprise – Clinical AI API for Healthcare" />
        <meta property="og:description" content="Integrate clinical-grade conversational AI into your platform. Trained on NIH & FDA datasets with HIPAA-compliant infrastructure." />
        <meta property="og:url" content="https://mycura.org/enterprise-api" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cura Enterprise – Clinical AI API for Healthcare" />
        <meta name="twitter:description" content="Integrate clinical-grade conversational AI into your platform. Trained on NIH & FDA datasets with HIPAA-compliant infrastructure." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Cura Enterprise API",
            "url": "https://mycura.org/enterprise-api",
            "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mycura.org/" },
              { "@type": "ListItem", "position": 2, "name": "Enterprise API", "item": "https://mycura.org/enterprise-api" }
            ]
          })}
        </script>
      </Helmet>

      <Navigation
        menuItems={menuItems}
        scrollToSection={scrollToSection}
        scrollToTop={scrollToTop}
        hideLogo={true}
        forceWhiteBackground={true}
        ctaText="Integrate Today"
        ctaHref="mailto:founders@mycura.org?subject=Enterprise%20Access%20Request"
        centerMenu={true}
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-48 md:pb-32 overflow-hidden bg-white">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-50 via-white to-white opacity-70"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center p-1 mb-8 rounded-full bg-blue-50 border border-blue-100">
            <span className="px-4 py-1 text-xs md:text-sm font-medium text-[#003c83] tracking-wide uppercase">Enterprise Edition</span>
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-['Instrument_Serif'] text-slate-900 mb-6 md:mb-8 leading-tight">
            Intelligence for <br />
            <span className="italic text-[#003c83]">modern healthcare.</span>
          </h1>

          <p className="text-lg md:text-xl lg:text-2xl text-slate-900 max-w-3xl mx-auto mb-8 md:mb-12 font-light leading-relaxed px-4">
            Integrate clinical-grade conversational AI into your platform. <br className="hidden md:block" />
            Trained on NIH & FDA datasets for unmatched accuracy.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 px-4">
            <a
              href="mailto:founders@mycura.org?subject=Enterprise%20Access%20Request"
              className="w-full sm:w-auto px-8 py-4 bg-[#003c83] text-white rounded-full font-medium text-lg hover:bg-[#002d62] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 text-center"
            >
              Request Access
            </a>
            <a
              href="https://docs.mycura.org"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 bg-white text-slate-700 border border-slate-200 rounded-full font-medium text-lg hover:bg-slate-50 transition-all text-center"
            >
              View Documentation
            </a>
          </div>
        </div>
      </section>

      {/* Trusted Data Section */}
      <section className="py-12 md:py-16 border-y border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-8">Trusted by data from</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
            {/* Replace with actual logos if available, using text for now as placeholders */}
            <span className="text-xl md:text-2xl font-bold text-slate-700">NIH</span>
            <span className="text-xl md:text-2xl font-bold text-slate-700">FDA</span>
            <span className="text-xl md:text-2xl font-bold text-slate-700">PubMed</span>
            <span className="text-xl md:text-2xl font-bold text-slate-700">Mayo Clinic</span>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 md:mb-24">
            <h2 className="text-4xl md:text-5xl font-['Instrument_Serif'] text-slate-900 mb-6">Core Capabilities</h2>
            <p className="text-lg md:text-xl text-slate-900 max-w-2xl leading-relaxed">
              Our API provides direct access to the same engines that power the Cura consumer app, optimized for high-throughput healthcare environments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Card 1 */}
            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-slate-100 hover:shadow-md transition-all group">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                <Activity className="w-7 h-7 text-[#003c83]" />
              </div>
              <h3 className="text-2xl md:text-3xl font-['Instrument_Serif'] text-slate-900 mb-4">Conversational Diagnostics</h3>
              <p className="text-slate-900 leading-relaxed mb-8">
                Embed our symptom checker directly into your EHR or patient portal. Supports natural language input and provides differential diagnoses with confidence scores.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-3" />
                  <span>Context-aware follow-up questions</span>
                </li>
                <li className="flex items-center text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-3" />
                  <span>Multi-turn dialogue management</span>
                </li>
                <li className="flex items-center text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-3" />
                  <span>Medical terminology normalization</span>
                </li>
              </ul>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-slate-100 hover:shadow-md transition-all group">
              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                <Database className="w-7 h-7 text-[#003c83]" />
              </div>
              <h3 className="text-2xl md:text-3xl font-['Instrument_Serif'] text-slate-900 mb-4">Pharmacological Intelligence</h3>
              <p className="text-slate-900 leading-relaxed mb-8">
                Access comprehensive drug interaction data, side effect profiles, and dosage guidelines. Real-time analysis of medication regimens.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-3" />
                  <span>Interaction checking (Drug-Drug, Drug-Food)</span>
                </li>
                <li className="flex items-center text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-3" />
                  <span>Contraindication alerts</span>
                </li>
                <li className="flex items-center text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-3" />
                  <span>Mechanism of action explanations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Developer Experience */}
      <section className="py-20 md:py-32 bg-[#0f172a] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6">
                <Code2 className="w-4 h-4 mr-2" />
                Developer First
              </div>
              <h2 className="text-4xl md:text-5xl font-['Instrument_Serif'] text-white mb-6">
                Built for <span className="italic text-blue-400">builders.</span>
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Our REST API is designed to be intuitive and powerful. Get up and running in minutes with comprehensive documentation, SDKs, and a sandbox environment.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Low Latency</h4>
                    <p className="text-slate-400">Optimized for real-time applications with sub-100ms response times.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Enterprise-Grade Security</h4>
                    <p className="text-slate-400">HIPAA compliant infrastructure with end-to-end encryption.</p>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <a href="https://docs.mycura.org" className="inline-flex items-center text-blue-400 hover:text-blue-300 font-medium transition-colors">
                  Read the docs <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>

            {/* Code Block */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl blur opacity-20"></div>
              <div className="relative bg-[#1e293b] rounded-2xl overflow-hidden border border-slate-700 shadow-2xl">
                <div className="flex items-center px-4 py-3 bg-[#0f172a] border-b border-slate-700">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="ml-4 text-xs text-slate-400 font-mono">POST /v1/diagnose</div>
                </div>
                <div className="p-6 overflow-x-auto">
                  <pre className="font-mono text-sm leading-relaxed">
                    <code className="language-json">
                      {`{
  "patient_context": {
    "age": 45,
    "gender": "male",
    "symptoms": [
      "chest_pain",
      "shortness_of_breath"
    ]
  },
  "options": {
    "include_differential": true,
    "confidence_threshold": 0.85
  }
}`}
                    </code>
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cura Scan Section */}
      <section className="py-20 md:py-32 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #003b82 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto mb-16 md:mb-24">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6">
              <Scan className="w-4 h-4 text-[#003b82]" />
              <span className="text-sm font-medium text-[#003b82]">New Feature</span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-['Instrument_Serif'] text-slate-900 mb-6 leading-tight">
              Cura <span className="italic">Scan</span>
            </h2>

            <p className="text-lg md:text-xl text-slate-900 mb-8 leading-relaxed max-w-2xl mx-auto">
              Intelligent medical imaging analysis that works alongside physicians to enhance diagnostic accuracy and patient care.
            </p>
          </div>

          {/* How It Works */}
          <div className="grid md:grid-cols-3 gap-8 mb-24">
            {[
              {
                step: "01",
                title: "Upload Imaging",
                description: "Securely upload X-rays, CT scans, MRIs, or other medical imaging directly through our HIPAA-compliant platform."
              },
              {
                step: "02",
                title: "AI Analysis",
                description: "Our advanced AI processes the imaging in seconds, identifying key findings, anomalies, and areas requiring attention."
              },
              {
                step: "03",
                title: "Review & Act",
                description: "Receive a comprehensive summary with highlighted findings, differential diagnoses, and recommendations for next steps."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative p-6 rounded-2xl bg-slate-50 border border-slate-100"
              >
                <div className="text-5xl md:text-6xl font-['Instrument_Serif'] text-blue-100 mb-4">{item.step}</div>
                <h3 className="text-xl md:text-2xl font-['Instrument_Serif'] text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-900 leading-relaxed text-sm md:text-base">{item.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {curaScanFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200 hover:shadow-lg transition-shadow"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-lg md:text-xl font-['Instrument_Serif'] text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-900 leading-relaxed text-sm md:text-base">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <ROICalculatorSection />

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-['Instrument_Serif'] text-slate-900 mb-6">
            Ready to transform <br />
            <span className="italic text-[#003c83]">your healthcare platform?</span>
          </h2>
          <p className="text-lg md:text-xl text-slate-900 mb-10 max-w-2xl mx-auto">
            Join the next generation of health tech companies building with Cura.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:founders@mycura.org?subject=Enterprise%20Access%20Request"
              className="w-full sm:w-auto px-8 py-4 bg-[#003c83] text-white rounded-full font-medium text-lg hover:bg-[#002d62] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 text-center"
            >
              Contact Sales
            </a>
            <a
              href="https://docs.mycura.org"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 bg-white text-slate-700 border border-slate-200 rounded-full font-medium text-lg hover:bg-slate-50 transition-all text-center"
            >
              Documentation
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
